import Controller, { inject as controller } from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import type { Hotel, Ota } from 'frontend-utils/models';
import type { Platform } from 'frontend-utils/utils/constants/constant-values';
import { PLATFORMS } from 'frontend-utils/utils/constants/constant-values';
import type MarketParityInsightController from 'lumen/controllers/market/parity/insight';
import type { RouterServiceWithLegacyTransitionTo } from 'lumen/initializers/router-legacy-transition-to';
import type { Period } from 'lumen/models';
import type MarketParityInsightHistoricalRoute from 'lumen/routes/market/parity/insight/historical';
import type { ExportDateObject } from 'lumen/routes/market/parity/insight/historical';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import type {
  MealTypeValues,
  RateParam,
} from 'lumen/utils/constants/general-constants';
import {
  MEALTYPE_TRANSLATION_KEYS,
  PLATFORM_TRANSLATION_KEYS,
} from 'lumen/utils/constants/general-constants';
import type {
  CountryCodeWithDefault,
  SelectInputOptionWithId,
} from 'lumen/utils/constants/pi-constant-values';
import {
  DEFAULT_POS,
  RATE_TYPE_TO_RATE_PARAM,
  RATE_TYPE_TRANSLATIONS,
} from 'lumen/utils/constants/pi-constant-values';
import type { ModelFrom } from 'lumen/utils/routes';

export default class MarketParityInsightHistoricalController extends Controller {
  declare model: ModelFrom<MarketParityInsightHistoricalRoute>;

  queryParams = ['exportDate', 'hotelId', 'platform'];

  @service declare intl: Services['intl'];
  @service declare router: RouterServiceWithLegacyTransitionTo;
  @service declare usageAnalytics: Services['usage-analytics'];

  @controller('market.parity.insight')
  declare parentController: MarketParityInsightController;

  @tracked exportDate: string | null = null;
  @tracked hotelId: string | null = null;
  @tracked platform: Platform =
    DEFAULT_QP_VALUES.market.parity.insight.historical.platform;

  get availableOTAs(): Ota[] {
    return this.hotel.getParityAnalyticsOTAs(this.platform);
  }

  get exportDateOptions(): SelectInputOptionWithId<string>[] {
    return this.model.exports.map((exportDate) => ({
      id: exportDate.extract_date,
      label: this.intl.t('parity_analytics.shop_range', {
        formattedExtractDate: dayjs(exportDate.extract_date)
          .startOf('week')
          .format('LL'),
        formattedExtractDateEndOfWeek: dayjs(exportDate.extract_date)
          .endOf('week')
          .format('LL'),
      }),
    }));
  }

  get filterSelections(): { los: number | null; persons: number | null } {
    const selectedExportDate = this.model.exports.find(
      (exportDate: ExportDateObject) =>
        exportDate.extract_date === this.exportDate,
    );

    if (selectedExportDate) {
      return {
        los: selectedExportDate.los,
        persons: selectedExportDate.persons,
      };
    }

    return { los: null, persons: null };
  }

  get filterString(): string {
    const filters: string[] = [];

    if (this.parentController.showMemberRate) {
      filters.push(this.intl.t('parity_strategy.membership.member'));
    }

    filters.push(
      this.intl.t(RATE_TYPE_TRANSLATIONS[this.parentController.rateType]),
    );

    if (this.filterSelections.los) {
      filters.push(
        this.intl.t('rates.nights', {
          count: this.filterSelections.los,
        }),
      );
    }

    if (this.filterSelections.persons) {
      filters.push(
        this.intl.t('rates.persons_selection', {
          count: this.filterSelections.persons,
        }),
      );
    }

    filters.push(
      this.intl.t(MEALTYPE_TRANSLATION_KEYS[this.parentController.mealPlan]),
    );

    if (this.parentController.pos !== DEFAULT_POS) {
      filters.push(
        `${this.intl.t('general.pos_short')}: ${this.parentController.pos}`,
      );
    }

    return filters.join(' - ');
  }

  get firstExportDateLabel(): string | undefined {
    return this.exportDateOptions[0]?.label;
  }

  get hasNoExportData(): boolean {
    return this.model.exports.length === 0;
  }

  get hotel(): Hotel {
    return this.model.hotel;
  }

  get hotelViewQuery(): {
    los: number | null;
    maxPersons: number | null;
    mealPlan: MealTypeValues;
    platform: Platform;
    pos: CountryCodeWithDefault;
    rateParam: RateParam;
    showMemberRate: boolean;
  } {
    const { mealPlan, pos, rateType, showMemberRate } = this.parentController;

    return {
      los: this.filterSelections.los,
      maxPersons: this.filterSelections.persons,
      platform: this.platform,
      rateParam: RATE_TYPE_TO_RATE_PARAM[rateType],
      mealPlan,
      pos,
      showMemberRate,
    };
  }

  get isLoading(): boolean {
    return (
      this.model.parities.isRunning || this.model.parityPermission.isRunning
    );
  }

  get liveParityHotelId(): string | null {
    if (this.model.parityPermission.isRunning) {
      return null;
    }

    return this.model.parityPermission.last?.value?.hotel_id ?? null;
  }

  get periods(): Period[] {
    return this.model.parities.last?.value ?? [];
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get platformOptions(): SelectInputOptionWithId<Platform>[] {
    return this.model.hotel.parityAnalyticsPlatforms.map((platform) => ({
      id: platform,
      label: this.intl.t(PLATFORM_TRANSLATION_KEYS[platform]),
    }));
  }

  get selectedExportDate(): SelectInputOptionWithId<string> | undefined {
    return this.exportDateOptions.find(
      (exportDate) => exportDate.id === this.exportDate,
    );
  }

  get selectedPlatformOption(): SelectInputOptionWithId<Platform> | undefined {
    return this.platformOptions.find(
      (platform) => platform.id === this.platform,
    );
  }

  get showMemberRate(): boolean {
    return this.parentController.showMemberRate;
  }

  get showMeta(): boolean {
    return this.platform !== PLATFORMS.PLATFORM_MOBILE_APP;
  }

  get showTable(): boolean {
    return !this.model.parities.isRunning && !this.hasNoExportData;
  }

  @action
  redirectOnClose(): void {
    this.router.legacyTransitionTo('market.parity.insight');
  }

  @action
  selectExportDate(selectedExportDate: SelectInputOptionWithId<string>): void {
    this.exportDate = selectedExportDate.id;
  }

  @action
  setPlatform(platformOption: SelectInputOptionWithId<Platform>): void {
    this.platform = platformOption.id;
  }

  @action
  trackRedirectToLiveView(hotelId: string): void {
    this.usageAnalytics.track('Live View Opened', {
      eventTrigger: 'live-view-link',
      hotelId,
    });
  }
}
